import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import { typography, colors } from 'styles/theme';
import { desktopUp, fluidTypography } from 'styles/utils';

const colorMap = {
  primary: colors.textColor,
  white: colors.white,
  purple: colors.darkPurple,
  inherit: 'inherit',
  brownGray: colors.brownGray,
};

const fontWeightMap = {
  normal: 300,
  medium: 400,
  bold: 500,
};

const mapVariantToStyle = {
  small: css`
    font-size: 1.4rem;
  `,
  p: css`
    line-height: 1.8;
  `,
  text: css`
    line-height: 1.5;
    font-size: 1.8rem;
  `,
  subTitle: css`
    line-height: 2.27;
    font-weight: 400;

    ${fluidTypography(2.2, 2.4)}

    ${desktopUp} {
      line-height: 1.29;
    }
  `,
  title: css`
    line-height: 1.25;
    font-weight: 500;

    ${fluidTypography(2.8, 4.2)}

    ${desktopUp} {
      line-height: 1.19;
    }
  `,
  serifTitle: css`
    line-height: 1.25;
    font-weight: 500;

    ${fluidTypography(2.8, 3.2)}

    ${desktopUp} {
      line-height: 1.56;
    }
  `,
};

const typographyStyle = ({ align, isSerif, color, weight, variant }) => {
  // If we have title variant and serif font we need to apply different style
  const localVariant = variant === 'title' && isSerif ? 'serifTitle' : variant;

  return css`
    text-align: ${align};
    font-family: ${isSerif ? typography.fontSerif : typography.fontPrimary};
    color: ${colorMap[color]};

    ${mapVariantToStyle[localVariant]};
    font-weight: ${weight && fontWeightMap[weight]};
  `;
};

/**
 * A generic component to control the typography in a limited set of type sizes
 * that work well together along with the layout.
 *
 * @param {object} props
 */
function Typography({
  children,
  className,
  component = 'div',
  variant = 'text',
  color = 'primary',
  align = 'inherit',
  weight = '',
  font = 'sans',
}) {
  const Component = component;
  const isSerif = font === 'serif';

  return (
    <Component
      css={typographyStyle({ align, isSerif, color, weight, variant })}
      className={className}
    >
      {children}
    </Component>
  );
}

Typography.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a React component.
   */
  component: PropTypes.elementType,
  /** Set the text-align on the component. */
  align: PropTypes.oneOf(['inherit', 'left', 'center', 'right', 'justify']),
  /** Applies the typography styles. */
  variant: PropTypes.oneOf(['title', 'subTitle', 'text', 'p', 'small']),
  /** Color of the component. */
  color: PropTypes.oneOf([
    'inherit',
    'brownGray',
    'primary',
    'white',
    'purple',
  ]),
  /** Set the font-weight on the component. */
  weight: PropTypes.oneOf(['normal', 'medium', 'bold']),
  /** Set the type of font-family (sans or serif) on the component. */
  font: PropTypes.oneOf(['serif', 'sans']),
};

export default Typography;
